import i18n from "i18next";
import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { dateColumn, enumColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import { PurchaseOrdersView } from "src/components/views/erp/purchase/purchaseOrders/PurchaseOrdersView.tsx";
import { OpenPurchaseOrderButton } from "src/components/views/erp/utilComponents/OpenPurchaseOrderButton.tsx";
import { PurchaseOrderProjectsApi } from "src/api/generated/erp/purchase/purchaseOrder/api/purchaseOrderProjectsApi.ts";
import { PurchaseOrderProjectView } from "src/api/generated/erp/db/types/tables/purchaseOrderProjectView.ts";
import { getPurchaseOrderProjectViewPurchaseOrderStateLabels } from "src/api/generated/erp/db/types/enums/purchaseOrderProjectViewPurchaseOrderState.ts";
import { faPlus, faUnlink } from "@fortawesome/pro-regular-svg-icons";
import { PurchaseOrderForm } from "src/components/views/erp/purchase/purchaseOrders/PurchaseOrderForm.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ProjectLinkedPurchaseOrdersDataGridProps {
	projectId: number;
}

export const ProjectLinkedPurchaseOrdersDataGrid = ({ projectId }: ProjectLinkedPurchaseOrdersDataGridProps) => {
	const showConfirmDialog = useConfirmDialog();
	const { openDialog } = useGenericDialog();

	return (
		<ClientSideDataGridModel<Array<PurchaseOrderProjectView>, PurchaseOrderProjectView, {}>
			gridId={"CF3D16A2662D1E1E"}
			fetchData={() => PurchaseOrderProjectsApi.getPurchaseOrdersForProject({ projectId })}
			getRows={(data) => data}
			getRowId={(row) => row.purchaseOrderProjectId}
			initialParams={{}}
			selectFirstRowOnLoad
			render={({ dataGridProps, onlySelectedRow, refreshData }) => (
				<AavoDataGrid<PurchaseOrderProjectView>
					columns={[
						integerColumn({
							field: "purchaseOrderId",
							headerName: i18n.t("number_shortened"),
						}),
						textColumn({
							field: "supplierName",
							headerName: i18n.t("supplier"),
							width: 180,
						}),
						enumColumn({
							field: "purchaseOrderState",
							headerName: i18n.t("state"),
							enumLabels: getPurchaseOrderProjectViewPurchaseOrderStateLabels(),
							width: 150,
						}),
						dateColumn({
							field: "plannedArrivalDate",
							headerName: i18n.t("planned_arrival_date"),
							width: 140,
						}),
						textColumn({
							field: "purchaseOrderReference",
							headerName: i18n.t("reference"),
							width: 200,
						}),
					]}
					actionBarComponents={
						<>
							<OpenPurchaseOrderButton
								purchaseOrderId={onlySelectedRow?.purchaseOrderId}
								variant={"outlined"}
							/>
							<AsyncButton
								label={i18n.t("create_purchase_order")}
								icon={faPlus}
								variant={"outlined"}
								onClick={() => {
									openDialog(({ closeDialog }) => ({
										title: i18n.t("purchase_order"),
										size: "xl",
										content: (
											<PurchaseOrderForm
												purchaseOrderId={undefined}
												createHandler={(request) =>
													PurchaseOrderProjectsApi.createPurchaseOrderForProject({
														request,
														projectId,
													})
												}
												onCompleted={async (result) => {
													await closeDialog();
													if (result.type === "success") {
														await refreshData();
													}
												}}
											/>
										),
										agentContext: {
											key: defineAgentUiContextKey("erp.purchase.purchaseOrder.formDialog"),
											title: i18n.t("purchase_order"),
											data: {
												projectId: projectId,
												purchaseOrderId: null,
											},
										},
									}));
								}}
							/>
							<AsyncButton
								label={i18n.t("unlink")}
								icon={faUnlink}
								variant={"outlined"}
								disabled={!onlySelectedRow}
								onClick={async () => {
									if (!onlySelectedRow) return;
									const confirmed = await showConfirmDialog();
									if (!confirmed) return;
									await PurchaseOrderProjectsApi.disconnectPurchaseOrderFromProject({
										purchaseOrderProjectId: onlySelectedRow.purchaseOrderProjectId,
									});
									await refreshData();
								}}
							/>
						</>
					}
					onRowDoubleClick={(params) => {
						openDialog({
							title: i18n.t("purchase_order"),
							size: "xl",
							content: <PurchaseOrdersView onlyPurchaseOrderId={params.row.purchaseOrderId} />,
							agentContext: {
								key: defineAgentUiContextKey("erp.purchase.purchaseOrder.dialog"),
								title: i18n.t("purchase_order"),
								pathSegments: [String(params.row.purchaseOrderId)],
								data: {
									projectId: projectId,
									purchaseOrderId: params.row.purchaseOrderId,
									purchaseOrderProjectId: params.row.purchaseOrderProjectId,
								},
							},
						});
					}}
					agentContext={{
						key: defineAgentUiContextKey("erp.project.linkedPurchaseOrders.grid"),
						data: {
							projectId: projectId,
						},
						rowFields: [
							"purchaseOrderProjectId",
							"purchaseOrderId",
							"supplierName",
							"purchaseOrderReference",
						],
						selectedRowPathSegment: (row) => String(row.purchaseOrderId),
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};
